/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, Params } from '@angular/router';
import { Location } from '@angular/common';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { iif, Observable, of, zip } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';
import { CookieService } from 'ngx-cookie-service';
import { isNullOrUndefined } from '@app/util/util';

import { flightsRootRoute } from '@flights/flights-routing.module';
import { AppFacade } from '@app/app.facade';
import { MODULES_ROUTING } from '@app/modules/core/mappers/modules.mapper';
import { flexiDatesRoute, flightResultsRootRoute } from '@flight-results/flight-results-routing.constants';
import { userAlertsRoute } from '@user-alerts/constants/user-alerts-routes.constants';
import {
  userHistoryDetailRoute, userHistoryRoute, cancellation, 
  lastReservation, userHistoryReservationCancellation, userHistoryHotelReservationCancellation,
  userHistoryAdditionalServices
} from '@user-history/constants/history-routes.constants';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { HOTEL_RESULTS_ROUTE } from '@hotels/hotel-results/constants/routes';
import { ReservationStateType } from '@shared/travel-etas/constants/travel-etas.constants';
import { busResultsBaseRoute } from '@buses/bus-results/constanst/bus-routes.constants';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';
import { User } from '@app/modules/core/interfaces/user';
import { HistoryItem } from '@app/entities/router-state.entity';
import { packagesResultRootRoute } from '@packages/packages-result/constants/packages-result.routes';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { packagesHomeRootRoute } from '@packages/packages-home/constants/packages.routes';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { BRANCH_CODE, SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { endCallWebView, redirectToLogin } from '@app/modules/core/util/redirect-login';
import { LoginType } from '@app/entities/login-type';
import { CheckoutWebSessionKey } from '@app/constants/checkout-web.constans';
import { environment as ENV } from '@environment';
import { BenefitsCookie } from '@app/constants/cookies';
import { productHomeRoute } from '@app/constants/app-routing.constants';

enum HeaderCurrentLocation {
  USER_HISTORY_LAST_RESERVATION = 'USER_HISTORY_LAST_RESERVATION',
  USER_HISTORY_DETAIL = 'USER_HISTORY_DETAIL',
  AUTOS_RESULT_SEARCH = 'AUTOS_RESULT_SEARCH',
  USER_HISTORY_PENDING = 'USER_HISTORY_PENDING',
  USER_HISTORY_CANCELLATION = 'USER_HISTORY_CANCELLATION',
  PACKAGE_HOTEL_RESULTS = 'PACKAGE_HOTEL_RESULTS',
  FLEX_DATES_HOME = 'FLEX_DATES_HOME',
  HOME = 'HOME'
}

@Injectable()
export class HeaderService {

  public homeRootRoute: string = '/';
  public parameterRouterOutlet: string = '(';

  private previousUrl: string;
  private currentUrl: string;
  private readonly isActiveMobileInterface: boolean;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly appFacade: AppFacade,
    private readonly _location: Location,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly searchFacade: BaseSearchBarFacade,
    private readonly cookieService: CookieService
  ) {
    this. isActiveMobileInterface = isActiveMobileInterface();
    router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (!event.url.includes(this.parameterRouterOutlet) &&
          this.currentUrl !== event.url) {
          this.previousUrl = this.currentUrl;
          this.currentUrl = event.url;
        }
      });
  }

  get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  private get closeWebViewInDetail$(): Observable<boolean> {
    return this.appFacade.closeWebViewInDetail$;
  }

  private get routes(): { [key: string]: string | string[] } {
    return MODULES_ROUTING;
  }

  private get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  private get isFlightBySector(): boolean {
    const params = this.currentRoute.split('/');
    return this.router.url.includes(flightResultsRootRoute) && params.length === 15;
  }

  private get isLocationBack(): boolean {
    if (!this.previousUrl) return false;
    if (
      this.isUserHistoryLastReservationUrl(this.currentUrl) ||
      this.isUserReservationCancellation(this.currentUrl) ||
      this.isUserHistoryDetailUrl(this.currentUrl) ||
      this.isUserHistoryListUrl()
    ) return false;
    if (this.currentUrl.includes(homeItemDetailRootRoute) && this.previousUrl.includes(homeItemDetailRootRoute)) return false;
    if (!this.previousUrl.includes(HOTEL_RESULTS_ROUTE) && !this.previousUrl.includes(flightResultsRootRoute)
      && !this.previousUrl.includes(busResultsBaseRoute)) return false;
    if (this.currentUrl.includes(flightResultsRootRoute) && this.previousUrl.includes(flightResultsRootRoute)) return false;
    if (this.currentUrl.includes(HOTEL_RESULTS_ROUTE) && this.previousUrl.includes(HOTEL_RESULTS_ROUTE)) return false;
    if (this.currentUrl.includes(busResultsBaseRoute) && this.previousUrl.includes(busResultsBaseRoute)) return false;
    if (this.currentUrl.includes(userAlertsRoute)) return false;
    return true;
  }

  public goBack(): void {
    const currentUrl = this.currentRoute;
    const mainScreenRoutes = Object.values(this.routes).filter(data => currentUrl === `/${data}`);

    switch (true) {
      case currentUrl === this.homeRootRoute:
        this.endCallWebView();
        break;
      case this.isFlightBySector:
        const route = currentUrl.split('/');
        route.splice(route.length - 2);
        this.router.navigateByUrl(`${route.join('/')}?${this.router.url.split('?')[1]}`);
        break;
      case (mainScreenRoutes.length > 0):
        this.router.navigate([this.homeRootRoute]);
        break;
      default:
        zip(this.appFacade.user$.pipe(take(1)), this.isMobile$.pipe(take(1))).pipe(
          switchMap(([user, isMobile]) => iif(
            () => currentUrl.includes(homeItemDetailRootRoute),
            zip(of({ user, isMobile, alternatingFlow: true }), this.closeWebViewInDetail$),
            zip(of({ user, isMobile, alternatingFlow: false }), of(null))
          )),
          map(([{ user, isMobile, alternatingFlow }, closeWebViewInDetail]) => ({
            user, isMobile, alternatingFlow, closeWebViewInDetail
          })),
          tap(({ alternatingFlow, closeWebViewInDetail, user, isMobile }) => {
            if (alternatingFlow && closeWebViewInDetail && this.isActiveMobileInterface) {
              this.endCallWebView();
            } else this.otherUrlBackAction(currentUrl, user, isMobile);
          }),
          switchMap(() => zip(
            this.appFacade.history$,
            this.route.queryParams.pipe(take(1))
          ).pipe(
            take(1),
            tap(([history, queryParams]) => this.navigateByHistory(queryParams, history))
          ))
        ).subscribe().unsubscribe();
        break;
    }
  }

  public forceNavigateByUrl(url: string, historyfindKey: string): void {
    zip(
      this.appFacade.history$.pipe(take(1)),
      this.searchFacade.branchCode$.pipe(take(1))
    ).pipe(
      tap(() => this.appFacade.historyReset()),
      map(([history, branchCode]) => ({ history, branchCode })),
      tap(({ history, branchCode }) => {
        const findInHistory = history?.find(item => item.url?.includes(historyfindKey));
        if (findInHistory) {
          this.router.navigate(url.split('/'), findInHistory?.extras);
        } else {
          this.router.navigate(url.split('/'), {
            queryParams: {
              [BRANCH_CODE]: branchCode, [SOURCE_PARAM]: EVENT_SOURCE.TRAVEL_HOME
            }
          });
        }
      })
    ).subscribe().unsubscribe();
  }

  public isUserHistoryListUrl(): boolean {
    const splitAll = this.currentRoute?.split('/')?.filter(item => !!item);
    if (splitAll?.length && splitAll[1]) {
      return /^\d+$/.test(splitAll[1]) && this.currentRoute?.endsWith(`${userHistoryRoute}/${splitAll[1]}`);
    } else return false;
  }

  public forceUserLogin(loginType: LoginType, currentUrl?: string): void {
    if (this.isActiveMobileInterface) {
      this.appFacade.setCloseWebviewModal(true);
    } else {
      this.removeSessionCookies();
      redirectToLogin(loginType, currentUrl);
    }
  }

  private removeSessionCookies(): void {
    this.cookieService.delete(CheckoutWebSessionKey.Id, '/', ENV.domain_checkout);
    this.cookieService.delete(CheckoutWebSessionKey.Type, '/', ENV.domain_checkout);
    this.cookieService.delete(CheckoutWebSessionKey.Id, '/', ENV.urlPath);
    this.cookieService.delete(CheckoutWebSessionKey.Type, '/', ENV.urlPath);
    this.cookieService.delete(BenefitsCookie);
  }

  private otherUrlBackAction(currentUrl: string, user: User, isMobile: boolean): void {
    const userId = user?.id || null;
    const resetHistory = this.resetAppHistory(currentUrl);
    const headerCurrentLocation = this.getHeaderCurrentLocation(currentUrl);
    
    if (resetHistory) this.appFacade.historyReset();
    switch (headerCurrentLocation) {
      case HeaderCurrentLocation.USER_HISTORY_LAST_RESERVATION:
      case HeaderCurrentLocation.USER_HISTORY_PENDING:
        if (userId) this.appFacade.historyPush(`${userHistoryRoute}/${userId}`);
        else this.appFacade.historyPush(this.homeRootRoute);
        break;
      case HeaderCurrentLocation.USER_HISTORY_DETAIL:
        if (this.previousUrl?.split('/').includes(ReservationStateType.Pending)) {
          this.appFacade.historyPush(`${userHistoryRoute}/${ReservationStateType.Pending}/${userId}`);
        } else if (!this.previousUrl || this.previousUrl?.split(`${cancellation}`)) {
          this.appFacade.historyPush(`${userHistoryRoute}/${userId}`);
        } else if (this.previousUrl) {
          this.appFacade.historyPush(this.previousUrl);
        }
        break;
      case HeaderCurrentLocation.USER_HISTORY_CANCELLATION:
        const split = this.currentRoute.split('/');
        const itineraryId = split[split.length - 2];
        const type = split[split.length - 1];
        if (itineraryId && type && userId) {
          this.appFacade.historyPush(`${userHistoryRoute}/${userHistoryDetailRoute}/${userId}/${itineraryId}/${type}`);
        } else this.appFacade.historyPush(this.homeRootRoute);
        break;
      case HeaderCurrentLocation.PACKAGE_HOTEL_RESULTS:
        const previous = isMobile ? `${packagesHomeRootRoute}/${productHomeRoute}` : this.homeRootRoute;
        this.appFacade.historyPush(previous);
        break;
      case HeaderCurrentLocation.FLEX_DATES_HOME:
        this.appFacade.historyPush(`${flightsRootRoute}/${productHomeRoute}`);
        break;
      default:
        if (!resetHistory) this.appFacade.historyPop();
        break;
    }
  }

  private resetAppHistory(currentUrl: string): boolean {
    return this.isUserHistoryLastReservationUrl(currentUrl) || this.isUserHistoryDetailUrl(currentUrl) ||
      this.isPendingReservations(currentUrl) || this.isUserReservationCancellation(currentUrl) ||
      (!currentUrl?.includes(homeItemDetailRootRoute) && (currentUrl?.includes(productHomeRoute)) ) 
      || this.isPackageHotelResults(currentUrl);
  }

  private isUserHistoryLastReservationUrl(currentUrl: string): boolean {
    return currentUrl?.includes(`${userHistoryRoute}/${lastReservation}`);
  }

  private isUserHistoryDetailUrl(currentUrl: string): boolean {
    return currentUrl?.includes(`${userHistoryRoute}/${userHistoryDetailRoute}`);
  }

  private isPendingReservations(currentUrl: string): boolean {
    return currentUrl?.split('/').includes(`${ReservationStateType.Pending}`);
  }

  private isUserReservationCancellation(currentUrl: string): boolean {
    return currentUrl?.includes(`${userHistoryRoute}/${userHistoryReservationCancellation}`) || 
    currentUrl?.includes(`${userHistoryRoute}/${userHistoryHotelReservationCancellation}`) ||
    currentUrl?.includes(`${userHistoryRoute}/${userHistoryAdditionalServices}`);
  }

  private isPackageHotelResults(currentUrl: string): boolean {
    return currentUrl?.includes(homeItemDetailRootRoute) && 
           currentUrl?.includes(packagesResultRootRoute) && currentUrl?.includes(PackageStep.HOTEL_RESULTS);
  }

  private isFlexDatesHome(currentUrl: string): boolean {
    return currentUrl?.includes(flexiDatesRoute) && 
    (
      this.previousUrl === this.homeRootRoute || 
      this.previousUrl === `/${flightsRootRoute}/${productHomeRoute}` ||
      this.previousUrl?.includes(flightResultsRootRoute) ||
      isNullOrUndefined(this.previousUrl)
    );
  }

  private getHeaderCurrentLocation(currentUrl: string): HeaderCurrentLocation {
    if (this.isUserHistoryLastReservationUrl(currentUrl)) {
      return HeaderCurrentLocation.USER_HISTORY_LAST_RESERVATION;
    } else if (this.isUserHistoryDetailUrl(currentUrl)) {
      return HeaderCurrentLocation.USER_HISTORY_DETAIL;
    } else if (this.isPendingReservations(currentUrl)) {
      return HeaderCurrentLocation.USER_HISTORY_PENDING;
    } else if (this.isUserReservationCancellation(currentUrl)) {
      return HeaderCurrentLocation.USER_HISTORY_CANCELLATION;
    } else if (this.isPackageHotelResults(currentUrl)) {
      return HeaderCurrentLocation.PACKAGE_HOTEL_RESULTS;
    } else if (this.isFlexDatesHome(currentUrl)) {
      return HeaderCurrentLocation.FLEX_DATES_HOME;
    } else {
      return HeaderCurrentLocation.HOME;
    }
  }

  private navigateByHistory(queryParams: Params, history: HistoryItem[], lastRouteNumber: number = 1): void {
    const lastRoute = history[history.length - lastRouteNumber];
    if (this.isLocationBack) {
      this._location.back();
    } else {
      const extras = isEmpty(lastRoute?.extras) ? { queryParams } : lastRoute?.extras;
      this.router.navigate([lastRoute.url], extras).catch(() => this.router.navigate([this.homeRootRoute]));
    }
  }

  private endCallWebView(): void {
    endCallWebView();
  }
}
